import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  ANALYTICS_RETAIL_EVENTS,
  classifyCatalogImageUrl,
  hashCatalogImagePath,
  isBrokenCatalogImageUrl,
  noteCatalogImageLoadFailure,
  resolveDonationDisplayImageUrl,
  resolveLocalizedName,
} from 'pi-kiosk-shared';
import { CatalogImagePlaceholder } from 'pi-kiosk-shared/ui';
import { useCustomerAnalytics } from '../../../shared/analytics/CustomerAnalyticsProvider.js';
import {
  emitDonationImpactOpened,
  resolveDonationProjectId,
} from '../../donation/donationAnalyticsMetadata.js';
import { Card } from '../../../shared/ui/Card.js';
import { Button } from '../../../shared/ui/Button.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import type { DonationProjectWithCollectedDTO } from '../donationProjectsApi.js';

function formatMinorAmount(amountMinor: number, currency: string, locale: string): string {
  const safeCurrency =
    typeof currency === 'string' && currency.trim().length >= 3
      ? currency.trim().toUpperCase()
      : 'CZK';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: safeCurrency,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100);
}

export interface DonationProjectCardProps {
  readonly project: DonationProjectWithCollectedDTO;
  readonly numberLocale: string;
  readonly onSelect: () => void;
}

export function DonationProjectCard({
  project,
  numberLocale,
  onSelect,
}: DonationProjectCardProps): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const displayName = resolveLocalizedName(project.name, project.nameLocales, i18n.language);
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  const analytics = useCustomerAnalytics();
  const [brokenImageUrl, setBrokenImageUrl] = useState<string | null>(null);
  const [impactExpanded, setImpactExpanded] = useState(false);
  const impactEmittedRef = useRef(false);
  const displayImageUrl = resolveDonationDisplayImageUrl(
    project.thumbnailUrl ?? null,
    project.imageUrl ?? null,
    { tenantCode },
  );
  const showImage =
    displayImageUrl !== null &&
    displayImageUrl.length > 0 &&
    brokenImageUrl !== displayImageUrl &&
    !isBrokenCatalogImageUrl(displayImageUrl);

  const goal = project.goalAmountMinor ?? null;
  const collected = project.collectedAmountMinor;
  const progress =
    goal !== null && goal > 0 ? Math.min(100, (collected / goal) * 100) : null;
  const hasImpactDetails = goal !== null || collected > 0;

  const openImpact = (): void => {
    if (!impactExpanded) {
      setImpactExpanded(true);
      if (!impactEmittedRef.current) {
        const projectId = resolveDonationProjectId(project.id);
        if (projectId !== undefined) {
          emitDonationImpactOpened(analytics.track, projectId);
          impactEmittedRef.current = true;
        }
      }
      return;
    }
    setImpactExpanded(false);
  };

  return (
    <Card padded>
      <div className="flex flex-col gap-3">
        <div className="relative h-32 w-full overflow-hidden rounded-md bg-[var(--color-surface-muted)]">
          {showImage ? (
            <img
              key={`${project.id}-${displayImageUrl ?? 'none'}`}
              src={displayImageUrl ?? ''}
              alt={displayName}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => {
                if (displayImageUrl !== null) {
                  setBrokenImageUrl(displayImageUrl);
                  const outcome = noteCatalogImageLoadFailure(displayImageUrl);
                  if (outcome.emitTelemetry) {
                    analytics.track({
                      eventName: ANALYTICS_RETAIL_EVENTS.CATALOG_IMAGE_LOAD_FAILED,
                      screenName: 'donation_projects',
                      metadata: {
                        tenantId: tenantCode,
                        donationProjectId: project.id,
                        httpStatusClass: 'unknown',
                        urlClass: classifyCatalogImageUrl(displayImageUrl),
                        surface: 'customer',
                        urlPathHash: hashCatalogImagePath(displayImageUrl),
                      },
                    });
                  }
                  if (outcome.refreshCatalog) {
                    void queryClient.invalidateQueries({
                      queryKey: customerQueryKeys.donationProjectsList(tenantCode),
                    });
                  }
                }
              }}
            />
          ) : (
            <CatalogImagePlaceholder label={displayName} className="h-full w-full" />
          )}
        </div>
        <div>
          <h3 className="text-base font-semibold text-[var(--color-on-surface)]">
            {displayName}
          </h3>
          {project.description !== null ? (
            <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
              {project.description}
            </p>
          ) : null}
        </div>
        {hasImpactDetails ? (
          <div>
            {!impactExpanded ? (
              <p className="text-xs text-[var(--color-on-surface-muted)]">
                {goal !== null
                  ? `${formatMinorAmount(collected, project.currency, numberLocale)} ${t('donation.collectedOf')} ${formatMinorAmount(goal, project.currency, numberLocale)}`
                  : t('donation.collected', {
                      amount: formatMinorAmount(collected, project.currency, numberLocale),
                    })}
              </p>
            ) : null}
            {impactExpanded ? (
              <div>
                {goal !== null ? (
                  <div className="flex items-baseline justify-between gap-2 text-xs text-[var(--color-on-surface-muted)]">
                    <span>{formatMinorAmount(collected, project.currency, numberLocale)}</span>
                    <span>
                      {t('donation.collectedOf')}{' '}
                      {formatMinorAmount(goal, project.currency, numberLocale)}
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--color-on-surface-muted)]">
                    {t('donation.collected', {
                      amount: formatMinorAmount(collected, project.currency, numberLocale),
                    })}
                  </p>
                )}
              </div>
            ) : null}
            {/* LOCKED #12 B / P10-G28: progressbar always paired with visible % (collapsed + expanded). */}
            {progress !== null ? (
              <div className="mt-1">
                <div
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress)}
                  aria-valuetext={t('donation.impactProgress', {
                    percent: Math.round(progress),
                  })}
                  className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-surface-muted)]"
                >
                  <div
                    className="h-full bg-[var(--color-primary)]"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-[var(--color-on-surface-muted)]">
                  {t('donation.impactProgress', { percent: Math.round(progress) })}
                </p>
              </div>
            ) : null}
            <Button intent="secondary" size="sm" block className="mt-2" onClick={openImpact}>
              {impactExpanded ? t('donation.hideImpact') : t('donation.viewImpact')}
            </Button>
          </div>
        ) : null}
        <Button intent="primary" size="md" block onClick={onSelect}>
          {t('donation.selectThisProject')}
        </Button>
      </div>
    </Card>
  );
}
